/*
 * Copyright (c) Dito Technologies
 */

import React, {Dispatch} from "react";
import StaffSelectPageMask from "ui/feature/root/staff-select/StaffSelectPageMask";
import {graphql} from "babel-plugin-relay/macro";
import {createFragmentContainer} from "react-relay";
import StaffSelectPageOverlayError from "ui/feature/root/staff-select/StaffSelectPageOverlayError";
import PageOverlayLoading from "ui/feature/root/common/PageOverlayLoading";
import {Action, SetSelectedBarAction, State} from "ui/feature/root/FormReducer";
import styles from "./StaffSelectPageOverlay.module.scss";
import {
    StaffSelectPageOverlay_sheet$data
} from "ui/feature/root/staff-select/__generated__/StaffSelectPageOverlay_sheet.graphql";

interface Props {
    sheet: StaffSelectPageOverlay_sheet$data;
    state: State;
    dispatch: Dispatch<Action>;
}

const StaffSelectPageOverlay: React.FC<Props> = ({state, sheet, dispatch}) => {

    const imageHeight = sheet.height;

    const isError: boolean = React.useMemo(() => {
        if (!sheet.bar_finder_steps) {
            return false;
        }
        return sheet.bar_finder_steps[0].status === "ERROR";
    }, [sheet]);

    const isLoading: boolean = React.useMemo(() => {
        if (!sheet.bar_finder_steps) {
            console.log("no bar finder steps", sheet);
            return true;
        }
        const step = sheet.bar_finder_steps[0];
        console.log("staff select", step);
        return !step.data
    }, [sheet]);

    const bars= React.useMemo(() => {
        if (!sheet.bar_finder_steps) {
            return [];
        }
        const step = sheet.bar_finder_steps[0];
        const data = step.data;
        if (!data) {
            return [];
        }
        return data.bars || [];
    }, [sheet]);

    const divRef = React.useRef<HTMLDivElement>(null)

    const divOnClickEvent = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {

        const barFinderSteps = sheet.bar_finder_steps;
        if (!barFinderSteps) {
            return;
        }
        const barFinderStepData = barFinderSteps[0].data;
        if (!barFinderStepData) {
            return;
        }
        const bars = barFinderStepData.bars;
        if (!bars) {
            return;
        }
        const currentImg = divRef.current;
        if (!currentImg) {
            return;
        }

        const height = currentImg.clientHeight;
        const xCoordinate = e.nativeEvent.offsetX;
        const yCoordinate = e.nativeEvent.offsetY;
        const adjustmentFactor = imageHeight / height;
        const adjustedX = xCoordinate * adjustmentFactor;
        const adjustedY = yCoordinate * adjustmentFactor;

        for (const bar of bars) {
            const minX = bar.x;
            const maxX = bar.x + bar.width;
            const minY = bar.y;
            const maxY = bar.y + bar.height;
            if (minX < adjustedX && adjustedX < maxX && minY < adjustedY && adjustedY < maxY) {
                const action: SetSelectedBarAction = {
                    type: "set_selected_bar",
                    barId: bar.bar_id
                }
                dispatch(action);
                break;
            }
        }

    }, [dispatch, imageHeight, sheet.bar_finder_steps]);

    if (isLoading) {
        return (
            <PageOverlayLoading/>
        );
    } else if (isError) {
        return (
            <StaffSelectPageOverlayError/>
        );
    } else {
        return (
            <div className={styles.overlayContainer} onClick={divOnClickEvent} ref={divRef}>
                {bars.map((bar, idx) => <StaffSelectPageMask state={state} idx={idx} key={bar.bar_id}
                                                             barFinderBar={bar}/>)}
            </div>
        );
    }
}

export default createFragmentContainer(StaffSelectPageOverlay, {
    sheet: graphql`
    fragment StaffSelectPageOverlay_sheet on MusicSheet {
        id
        width
        height
        bar_finder_steps: steps(step_type: BAR_FINDER) {
            id 
            status
            data {
                ... on MusicParseStepDataBarFinder {
                    bars {
                        bar_id: mask_storage_name
                        x
                        y
                        width
                        height
                        ...StaffSelectPageMask_barFinderBar
                    }
                }
            }
        }
         bar_finder_user_confirmation_steps: steps(step_type: BAR_FINDER_USER_CONFIRMATION) {
            id
            status
        }
    }
    `
});
